import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { ArticleCard, HomepageSectionMeta } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { publicApi } from '@/lib/api-client';
import { queryKeys, STALE } from '@/lib/query';
import {
  AdSlot,
  CategoryExplorer,
  CultureBlock,
  HeroCarousel,
  LatestSection,
  LivePreview,
  NewsletterBlock,
  TopStoryBand,
  TrendingList,
  VideoCoverage,
  type HeroSettings,
} from '@/components/home';
import { Icon } from '@/components/shell';
import { ErrorState } from '@/components/ui';
import { CardGridSkeleton, HeroSkeleton, ListSkeleton, Section } from './page-parts';

/**
 * The homepage — PRD §6–§12, every section from `/api/v1/public/homepage`.
 *
 * Two things make this page what Phase 8 asked for rather than a hard-coded arrangement
 * that happens to read from an API:
 *
 * - **The section order comes from the payload.** `homepage_sections` is per locale and
 *   editable in the CMS (PRD §23), so the Assamese front page can lead with hero →
 *   ranked → categories while the English one leads with hero → trending. This component
 *   renders whatever order it is handed and hides what an editor turned off; it never
 *   decides.
 * - **The data was already resolved on the server.** `ssr-data.ts` calls `getHomepage`
 *   in-process and seeds the query cache from the serialized payload, so this `useQuery`
 *   finds fresh data on the first client render and does not refetch. The skeletons below
 *   are for the case that fails — a cold client-side navigation, or an SSR data resolver
 *   that timed out.
 */
export default function Home() {
  const { locale, t, path } = useLocale();

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: queryKeys.homepage(locale),
    queryFn: () => publicApi.homepage(locale),
    staleTime: STALE.homepage,
  });

  if (isError) {
    return (
      <div className="px-gutter py-16">
        <ErrorState
          title={t('error.genericTitle')}
          body={t('error.genericBody')}
          retryLabel={t('action.retry')}
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  if (isPending || !data) return <HomeSkeleton />;

  const bySection = new Map(data.sections.map((section) => [section.section, section]));
  const ordered = [...data.sections]
    .filter((section) => section.isVisible)
    .sort((a, b) => a.position - b.position);

  const viewAll = (to: string) => (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 text-sm font-bold text-ink transition hover:text-red"
    >
      {t('action.viewAll')}
      <Icon name="chevron-right" size={13} />
    </Link>
  );

  const render = (section: HomepageSectionMeta): ReactNode => {
    switch (section.section) {
      // The lead story, drawn full-bleed rather than inside a `Section`: it is the page's
      // opening statement, not an entry in a list of them. Nothing renders when the desk
      // has not flagged one, which is also what an editor hiding the section gets.
      case 'top_story':
        return data.topStory ? (
          <TopStoryBand key={section.section} article={data.topStory} />
        ) : null;

      case 'hero':
        return (
          <HeroCarousel
            key={section.section}
            slides={data.hero}
            ranked={data.ranked}
            settings={heroSettings(bySection.get('hero'))}
            priority={!leadsWithTopStory(ordered, data.topStory)}
          />
        );

      // The ranked column is rendered *inside* the hero (PRD §7 binds the two), so its
      // own section entry only controls whether the column appears at all.
      case 'ranked':
        return null;

      case 'categories':
        return (
          <Section key={section.section} title={t('section.categories')}>
            <CategoryExplorer categories={data.categories} />
          </Section>
        );

      case 'latest':
        return (
          <Section
            key={section.section}
            title={t('section.latest')}
            action={viewAll(path('trending'))}
          >
            <LatestSection articles={data.latest.items} filters={data.latest.filters} />
          </Section>
        );

      case 'trending':
      case 'culture':
      case 'live':
        // The design puts trending, the culture feature and live coverage in one
        // three-column row. Whichever of the three comes first in the editor's order
        // draws the row; the others fold into it.
        return section.section === firstOfTrio(ordered) ? (
          <Section key="trio">
            <div className="grid gap-5 lg:grid-cols-tri">
              {isVisible(bySection, 'trending') && (
                <div>
                  <h2 className="mb-4 text-xl font-extrabold text-ink">{t('section.trending')}</h2>
                  <TrendingList articles={data.trending} />
                </div>
              )}
              {isVisible(bySection, 'culture') && (
                <div>
                  <h2 className="mb-4 text-xl font-extrabold text-ink">{t('section.culture')}</h2>
                  <CultureBlock
                    feature={data.culture.feature}
                    supporting={data.culture.supporting}
                  />
                </div>
              )}
              {isVisible(bySection, 'live') && (
                <div>
                  <h2 className="mb-4 text-xl font-extrabold text-ink">
                    {t('section.liveUpdates')}
                  </h2>
                  <LivePreview events={data.liveEvents} />
                </div>
              )}
            </div>
          </Section>
        ) : null;

      case 'videos':
        // `VideoCoverage` returns null when there is nothing published, and a section
        // heading over an empty grid is worse than no section — so the check is here, not
        // only inside it.
        return data.videos.length > 0 ? (
          <Section
            key={section.section}
            title={t('section.videoCoverage')}
            action={viewAll(path('videos'))}
          >
            <VideoCoverage videos={data.videos} />
          </Section>
        ) : null;

      case 'newsletter': {
        const sidebar = data.ads.find((ad) => ad.placement === 'sidebar');
        return (
          <Section key={section.section}>
            <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
              <NewsletterBlock />
              {sidebar && <AdSlot slot={sidebar} className="self-center" />}
            </div>
          </Section>
        );
      }

      case 'ad':
        return null; // Ads are placed against the design's own slots, below.

      default:
        return null;
    }
  };

  const leaderboard = data.ads.find((ad) => ad.placement === 'top-leaderboard');
  const banner = data.ads.find((ad) => ad.placement === 'homepage-banner');

  return (
    <>
      {/*
        The document's one `h1`. It is hidden rather than drawn because the supplied
        design has no page title above the hero — but a homepage with no `h1`, whose
        first heading is a carousel slide that changes every seven seconds, gives a
        screen-reader user nothing stable to orient by and a crawler nothing to index.
      */}
      <h1 className="sr-only">
        {t('brand.name')} — {t('brand.tagline')}
      </h1>
      {ordered.map((section) => {
        const body = render(section);
        // The design hangs the leaderboard directly under the hero block, and the banner
        // under the latest grid.
        const ad =
          section.section === 'hero' && leaderboard ? (
            <div className="bg-soft px-gutter pb-5">
              <AdSlot slot={leaderboard} />
            </div>
          ) : section.section === 'latest' && banner ? (
            <div className="px-gutter pb-2">
              <AdSlot slot={banner} />
            </div>
          ) : null;

        // A section that renders nothing must not reserve `contain-intrinsic-size` worth
        // of empty page — `ranked` and `ad` both fold into other sections, and an editor
        // can hide any of them.
        if (!body && !ad) return null;

        return (
          <SectionFrame key={section.section} section={section.section}>
            {body}
            {ad}
          </SectionFrame>
        );
      })}
    </>
  );
}

/**
 * Everything below the hero gets `content-visibility: auto` (Phase 8 task list), so the
 * browser skips layout and paint for sections that are off-screen.
 *
 * Two details decide whether that is a win or a nuisance:
 *
 * - **`contain-intrinsic-size: auto <estimate>`.** The `auto` keyword tells the browser
 *   to remember each section's real height once it has rendered it, so the page stops
 *   resizing after the first pass. Without it, every scroll past a section snaps the
 *   scrollbar by the difference between the guess and the truth.
 * - **Per-section estimates.** A single 600px guess is wrong by 300px on the video strip
 *   and by 400px on the latest grid, which is exactly the jump `auto` then has to
 *   correct. These are measured from the rendered page.
 */
const INTRINSIC_HEIGHT: Record<string, number> = {
  categories: 220,
  latest: 900,
  trending: 780,
  culture: 780,
  live: 780,
  videos: 520,
  newsletter: 380,
};

function SectionFrame({ section, children }: { section: string; children: ReactNode }) {
  // The top story and the hero are above the fold in every layout, so skipping their
  // paint would be a loss, not a saving.
  if (section === 'top_story' || section === 'hero') return <>{children}</>;
  return (
    <div
      style={{
        contentVisibility: 'auto',
        containIntrinsicSize: `auto ${INTRINSIC_HEIGHT[section] ?? 600}px`,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Whether the top-story band is drawn *and* drawn first.
 *
 * An editor can move the band below the hero or hide it, and either way the hero is back
 * to being the page's leading image — so the eager load follows the arrangement rather
 * than assuming one.
 */
function leadsWithTopStory(ordered: HomepageSectionMeta[], topStory: ArticleCard | null): boolean {
  if (!topStory) return false;
  const first = ordered.find(
    (section) => section.section === 'top_story' || section.section === 'hero',
  );
  return first?.section === 'top_story';
}

function isVisible(
  sections: Map<string, HomepageSectionMeta>,
  name: 'trending' | 'culture' | 'live',
): boolean {
  return sections.get(name)?.isVisible ?? false;
}

/** Which of the three-column trio an editor put first. */
function firstOfTrio(ordered: HomepageSectionMeta[]): string | undefined {
  return ordered.find((section) => ['trending', 'culture', 'live'].includes(section.section))
    ?.section;
}

/** PRD §6's rotation settings, stored on the hero section by the Phase 5 curation API. */
function heroSettings(section: HomepageSectionMeta | undefined): HeroSettings {
  const settings = section?.settings ?? {};
  const autoRotate = settings['autoRotate'];
  const seconds = settings['rotationSeconds'];
  return {
    autoRotate: typeof autoRotate === 'boolean' ? autoRotate : true,
    rotationSeconds: typeof seconds === 'number' && seconds > 0 ? seconds : 7,
  };
}

function HomeSkeleton() {
  const { t } = useLocale();
  return (
    <>
      <HeroSkeleton label={t('status.loading')} />
      <Section title={t('section.categories')}>
        <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-4 lg:grid-cols-7">
          {Array.from({ length: 7 }, (_, index) => (
            <div key={index} className="nk-skeleton h-[104px] rounded-lg" aria-hidden="true" />
          ))}
        </div>
      </Section>
      <Section title={t('section.latest')}>
        <CardGridSkeleton count={4} label={t('status.loading')} />
      </Section>
      <Section>
        <div className="grid gap-5 lg:grid-cols-tri">
          <ListSkeleton rows={5} label={t('status.loading')} />
          <ListSkeleton rows={3} label={t('status.loading')} />
          <ListSkeleton rows={4} label={t('status.loading')} />
        </div>
      </Section>
    </>
  );
}
